import { useRef, useCallback } from 'react'

type ErrorObject = Record<string, any>

export const getFirstErrorPath = (
  errors: ErrorObject | null | undefined
): string | null => {
  if (!errors || typeof errors !== 'object') return null

  const traverse = (obj: ErrorObject, prefix = ''): string | null => {
    if (!obj || typeof obj !== 'object') return null
    const keys = Object.keys(obj)
    for (const key of keys) {
      const value = obj[key]
      const path = prefix ? `${prefix}.${key}` : key
      if (value && typeof value === 'object' && (value.message || value.type)) {
        return path
      }
      if (value && typeof value === 'object') {
        const nested = traverse(value, path)
        if (nested) return nested
      }
    }
    return null
  }

  return traverse(errors as ErrorObject)
}

type FieldRefs = Record<string, Element | null>

export const useFormErrorScroller = (
  contentRef?: { current: Element | null } | null
) => {
  const fieldRefs = useRef<FieldRefs>({})

  const registerField = useCallback((name: string, el: Element | null) => {
    if (!name) return
    fieldRefs.current[name] = el
  }, [])

  const resetFields = useCallback(() => {
    fieldRefs.current = {}
  }, [])

  const scrollToField = useCallback(
    (fieldName: string | null) => {
      if (!fieldName) return

      const exactRef = fieldRefs.current[fieldName]
      let el: Element | null = exactRef || null

      if (!el && fieldName.includes('.')) {
        const last = fieldName.split('.').pop() as string
        el = fieldRefs.current[last] || null
      }

      if (!el) {
        try {
          el = document.querySelector(
            `[data-field-name="${fieldName}"]`
          ) as Element | null
        } catch {
          el = null
        }
      }

      if (!el) {
        const last = fieldName.includes('.')
          ? (fieldName.split('.').pop() as string)
          : fieldName
        el = document.querySelector(
          `[data-field-name="${last}"]`
        ) as Element | null
      }

      if (!el) {
        el = document.querySelector(`[name="${fieldName}"]`) as Element | null
      }

      if (!el) {
        el = document.querySelector('[aria-invalid="true"]') as Element | null
      }

      if (!el) return

      if (contentRef && contentRef.current) {
        const content = contentRef.current as Element
        const elRect = el.getBoundingClientRect()
        const contentRect = content.getBoundingClientRect()
        const offset =
          elRect.top - contentRect.top + (content as any).scrollTop - 24
        try {
          ;(content as any).scrollTo({ top: offset, behavior: 'smooth' })
        } catch {
          ;(content as any).scrollTop = offset
        }
      } else {
        ;(el as HTMLElement).scrollIntoView({
          behavior: 'smooth',
        })
      }

      const focusable = (el as HTMLElement).querySelector
        ? ((el as HTMLElement).querySelector(
            'input,textarea,button,select'
          ) as HTMLElement | null)
        : null

      const doFocus = () => {
        try {
          if (focusable && typeof (focusable as any).focus === 'function') {
            ;(focusable as any).focus({ preventScroll: true })
          } else if ((el as HTMLElement).focus) {
            ;(el as HTMLElement).focus({ preventScroll: true } as any)
          }
        } catch {
          if (focusable && typeof focusable.focus === 'function') {
            focusable.focus()
          } else if ((el as HTMLElement).focus) {
            ;(el as HTMLElement).focus()
          }
        }
      }

      setTimeout(doFocus, 300)
    },
    [contentRef]
  )

  return {
    getFirstErrorPath,
    scrollToField,
    registerField,
    resetFields,
    fieldRefs: fieldRefs.current,
  }
}

export default useFormErrorScroller
